import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, Save, X, Truck, Download } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import LoadingSpinner from '../../components/LoadingSpinner';

export default function AdminShippingZones() {
  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    source_region: '',
    destination_state: '',
    weight_from: 0,
    weight_to: 250,
    charges: 0
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [zonesRes, regionsRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/api/admin/shipping-zones`),
        axiosInstance.get(`${API_URL}/api/admin/broader-regions`)
      ]);
      setZones(zonesRes.data);
      setRegions(regionsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load shipping zones');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.source_region || !formData.destination_state || formData.charges < 0) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editingId) {
        await axiosInstance.put(`${API_URL}/api/admin/shipping-zones/${editingId}`, formData);
        toast.success('Shipping zone updated');
      } else {
        await axiosInstance.post(`${API_URL}/api/admin/shipping-zones`, formData);
        toast.success('Shipping zone created');
      }
      fetchData();
      handleCancel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shipping zone');
    }
  };

  const handleEdit = (zone) => {
    setFormData({
      source_region: zone.source_region,
      destination_state: zone.destination_state,
      weight_from: zone.weight_from,
      weight_to: zone.weight_to,
      charges: zone.charges
    });
    setEditingId(zone._id || zone.id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this shipping zone?')) return;
    try {
      await axiosInstance.delete(`${API_URL}/api/admin/shipping-zones/${id}`);
      toast.success('Shipping zone deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleCancel = () => {
    setFormData({ source_region: '', destination_state: '', weight_from: 0, weight_to: 250, charges: 0 });
    setEditingId(null);
    setIsEditing(false);
  };

  if (loading) return (
    <AdminLayout>
      <div className="flex justify-center items-center h-screen"><LoadingSpinner size="lg" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shipping Zones</h1>
            <p className="text-gray-600 mt-1">Configure region-wise shipping charges by weight</p>
          </div>
          <Button onClick={() => setIsEditing(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Add Shipping Zone
          </Button>
        </div>

        {isEditing && (
          <Card className="border-2 border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle>{editingId ? 'Edit' : 'Add'} Shipping Zone</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label>Source Region *</Label>
                  <select
                    value={formData.source_region}
                    onChange={(e) => setFormData({...formData, source_region: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Region</option>
                    {regions.map(r => <option key={r._id || r.id} value={r.code}>{r.code} - {r.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Destination State *</Label>
                  <select
                    value={formData.destination_state}
                    onChange={(e) => setFormData({...formData, destination_state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select State</option>
                    {indianStates.map(state => <option key={state} value={state}>{state}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Weight From (g)</Label>
                  <Input
                    type="number"
                    value={formData.weight_from}
                    onChange={(e) => setFormData({...formData, weight_from: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Weight To (g)</Label>
                  <Input
                    type="number"
                    value={formData.weight_to}
                    onChange={(e) => setFormData({...formData, weight_to: parseFloat(e.target.value) || 250})}
                  />
                </div>
                <div>
                  <Label>Charges (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.charges}
                    onChange={(e) => setFormData({...formData, charges: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={handleCancel}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Configured Zones ({zones.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Destination</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Weight Range</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Charges</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {zones.map(zone => (
                    <tr key={zone._id || zone.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{zone.source_region}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{zone.destination_state}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{zone.weight_from}g - {zone.weight_to}g</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">₹{zone.charges}</td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(zone)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(zone._id || zone.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {zones.length === 0 && (
              <div className="text-center py-12">
                <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Shipping Zones</h3>
                <p className="text-gray-600 mb-4">Add zones to configure shipping charges</p>
                <Button onClick={() => setIsEditing(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" /> Add First Zone
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
